import type { HoldingsResponse, SourceState } from '~/api/client'
import { api, apiFetch } from '~/api/client'
import type { NetWorthSeries, PointCoverage } from './series'

/** Well past any history this database can hold, so one request covers every range. */
const ALL_DAYS = 3650

export interface NetWorthSnapshot {
  date: string
  baseCurrency: string
  lookThroughMinor: number
  spendableMinor: number
  liquidMinor: number
  taxMinor: number
  taxRatesConfigured: boolean
  projectedMinor: number
  personalMinor: number
  companyMinor: number
  /** Whether anything is held through an entity other than the household itself. */
  holdsThroughCompany: boolean
  /** Whether anything held cannot be sold; where false, Liquid repeats the figure before it. */
  holdsUnsellable: boolean
  flags: {
    stale: boolean
    projected: boolean
    staleSince: string | null
    oldestPositionDate: string | null
    oldestPriceDate: string | null
    unpricedInstrumentIds: number[]
    missingFx: string[]
    carriedFx: string[]
    coverage: PointCoverage & { knownSources: string[] }
  }
  sources: SourceState[]
}

export function fetchNetWorthSeries(): Promise<NetWorthSeries> {
  return apiFetch<NetWorthSeries>(`/networth/series?days=${ALL_DAYS}`)
}

/**
 * A function of its own rather than a parameter on the one above, which is
 * handed to `useQuery` bare and would be called with the query's context as
 * the view to ask for.
 */
export function fetchSellableNetWorthSeries(): Promise<NetWorthSeries> {
  return apiFetch<NetWorthSeries>(`/networth/series?days=${ALL_DAYS}&holdings=sellable`)
}

export function fetchNetWorthSnapshot(): Promise<NetWorthSnapshot> {
  return apiFetch<NetWorthSnapshot>('/networth/current')
}

export function fetchHoldings(date: string): Promise<HoldingsResponse> {
  return api.holdings(date)
}

export function isNoData(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    (error as { status: unknown }).status === 404
  )
}
